import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Checkbox,
  Stack,
  Typography,
  Alert,
  Snackbar,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import Loader from "@shared/loader";
import NameValuePair from "./nameValuePair";
import UserDetails from "@shared/userDetails";
import StickyActionContainer from "@shared/stickyActionContainer";
import { IAllData } from "../KYCsteps";
import { resetError, submitKYC } from "../../../../store/slices/submitKYCSlice";
import {
  CheckboxContainer,
  NameValuePairSection,
} from "./confirmDetails.styles";
import { getUrl } from "utils/core";
import { APIResource, KYCConstants } from "constants/application";
import {
  IGenericInformation,
  setGenericInformation,
  setKycDone,
} from "store/slices/genericInformation";
import axiosInstance from "utils/axiosInstance";
import { ROUTES_VARIANT } from "constants/routesVariant";
import ConfirmPagePanNote from "./confirmPagePanNote";
import NameMismatch from "./nameMismatch/nameMismatch";
import { AppSpecificName } from "constants/headerImage";

// Final step of KYC verification
function ConfirmDetails() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const kycStatus = useSelector((store: any) => store.kycStatus);
  const [reasonForNameMismatch, setReasonForNameMismatch] =
    useState<string>("");
  const info = useSelector(
    (state: any) => state.info.allData
  ) as IGenericInformation;
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isConfirmed, setIsConfirmed] = useState(false);

  const submitHandler = () => {
    const apiData = {
      nameMismatchReason: reasonForNameMismatch,
      customerSource: ''
    };
    dispatch(submitKYC(apiData) as any);
  };

  useEffect(() => {
    if (kycStatus.success) {
      dispatch(setKycDone(true) as any);
      navigate(`/${ROUTES_VARIANT.FINISH}`, { replace: true });
    }
  }, [kycStatus]);

  const editDetails = () => {
    setSearchParams(
      {
        step: "1",
      },
      { replace: true }
    );
  };

  useEffect(() => {
    try {
       const appName:any = localStorage.getItem('appname');
     

      const getCustomerDetails = async () => {
        const response = await axiosInstance.get(
          getUrl(APIResource.customerDetails, [
            { variableName: "customerId", variableValue: info?.customerId },
          ], [{paramName: "customerSource", paramValue: AppSpecificName[appName]}])
        );
        if (response.status === 200) {
          dispatch(setGenericInformation(response.data));
        }
      };

      getCustomerDetails();
    } catch (err) {
      console.log("Error Occurred ", err);
    }
  }, []);

  const reasonChange = (text: string) => {
    setReasonForNameMismatch(text);
  };
  return (
    <Stack direction={"column"} flexGrow={1}>
      <UserDetails type="CONFIRM" />

      <NameValuePairSection>
        <ConfirmPagePanNote />

        {info?.panDetails?.panNumber && (
          <>
            <NameValuePair
              name={t("pan_number", { defaultValue: "Pan Number" })}
              value={info?.panDetails?.panNumber}
            />
            <NameValuePair
              name={t("name_on_pan_card", { defaultValue: "Name on Pan Card" })}
              value={info?.panDetails?.fullName}
            />
          </>
        )}

        <NameValuePair
          name={t("establishment_type", { defaultValue: "Establishment Type" })}
          value={info?.establishmentType}
        />
        <NameValuePair
          name={t("bank_name", { defaultValue: "Bank Name" })}
          value={info?.bankDetails?.bankName}
        />

        <NameValuePair
          name={t("account_number", { defaultValue: "Account Number" })}
          value={info?.bankDetails?.bankAccountNumber}
        />
        <NameValuePair
          name={t("acc_holder", { defaultValue: "Account Holder" })}
          value={info?.bankDetails?.accountHolderName}
        />
        <NameValuePair
          name={t("acc_type", { defaultValue: "Account Type" })}
          value={info?.bankDetails?.accountType}
        />
        <NameValuePair
          name={t("ifsc_code", { defaultValue: "IFSC Code" })}
          value={info?.bankDetails?.ifscCode}
        />
      </NameValuePairSection>
      <NameMismatch reason={reasonChange} />
      <CheckboxContainer>
        <Checkbox
          size="large"
          name="confirm"
          sx={{ paddingTop: 0 }}
          checked={isConfirmed}
          onChange={(e) => setIsConfirmed(e.target.checked)}
        />
        <Typography variant="body1" color="#666666">
          {t("confirmation_text", {
            userID: info?.customerId,
            defaultValue: `I Confirm that all the details furnished are correct and are related to ${info?.customerId}  customer ID. Payment can be done, TDS can be deducted & submitted as per the details furnished.`,
          })}
        </Typography>
      </CheckboxContainer>
      <StickyActionContainer>
        <Button
          size="large"
          disabled={
            !isConfirmed ||
            !(info?.panDetails &&
            Number(info?.bankDetails?.userNameMatchScore) <
              Number(process.env.REACT_APP_NAME_MATCH_SCORE_LIMIT)
              ? Boolean(reasonForNameMismatch)
              : true)
          }
          fullWidth
          variant="contained"
          onClick={submitHandler}
        >
          {t("submit", { defaultValue: "Submit" })}
        </Button>
        <Button size="large" onClick={editDetails} fullWidth variant="outlined">
          {t("edit_details", { defaultValue: "Edit Details" })}
        </Button>
      </StickyActionContainer>
      {kycStatus.loading && <Loader />}
      <Snackbar
        open={kycStatus?.error}
        onClose={() => dispatch(resetError() as any)}
        autoHideDuration={KYCConstants.autoHideDuration}
        message={t("error_occurred", { defaultValue: "Error Occurred!" })}
        sx={{ bottom: { xs: 90, sm: 0 } }}
      />
    </Stack>
  );
}

export default ConfirmDetails;
